import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { type JobType } from "@src/appV2/redesign/PlacementCandidate/types";
import { Divider, Text } from "@src/appV2/redesign/system/ui-components";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { IosSwitch } from "../../../components/IosSwitch";
import { type PlacementForComputedPay } from "../../lib/usePayTransparencyRates";
import { type TransparencyPayRate } from "../../types/payRate";
import { getOfferedJobTypes } from "../../utils/pay-transparency";
import { type PayAndScheduleFormData } from "../PayAndScheduleForm/usePayAndScheduleForm";

interface BenefitsDiffCardProps {
  transparencyPayRates: TransparencyPayRate[];
  hasSplitBenefitsDiff: boolean;
  selectedJobType: JobType;
  placement: PlacementForComputedPay;
  placementCandidateId: string;
  workerId: string;
  highestMatchingQualification: string;
  isUpdating: boolean;
  onBenefitsChange: (
    formData: Pick<
      PayAndScheduleFormData,
      "useCombinedBenefit" | "useHealthInsuranceBenefit" | "usePaidTimeOffBenefit"
    >
  ) => void;
}

export function BenefitsDiffCardV2({
  transparencyPayRates,
  hasSplitBenefitsDiff,
  selectedJobType,
  placement,
  placementCandidateId,
  workerId,
  highestMatchingQualification,
  isUpdating,
  onBenefitsChange,
}: BenefitsDiffCardProps) {
  const { control, watch, setValue } = useFormContext<PayAndScheduleFormData>();

  const useHealthInsuranceBenefit = watch("useHealthInsuranceBenefit");
  const usePaidTimeOffBenefit = watch("usePaidTimeOffBenefit");

  const filteredTransparencyPayRates = useMemo(() => {
    return transparencyPayRates.filter((payRate) => payRate.jobTypes.includes(selectedJobType));
  }, [transparencyPayRates, selectedJobType]);

  const requiredCombinedBenefits =
    filteredTransparencyPayRates.length > 0 &&
    filteredTransparencyPayRates.every((payRate) => !isDefined(payRate.benefitsDiff)) &&
    !hasSplitBenefitsDiff;

  const requiredHealthBenefits =
    filteredTransparencyPayRates.length > 0 &&
    filteredTransparencyPayRates.every((payRate) => !isDefined(payRate.healthBenefitsDiff)) &&
    hasSplitBenefitsDiff;

  const requiredPaidTimeOffBenefits =
    filteredTransparencyPayRates.length > 0 &&
    filteredTransparencyPayRates.every((payRate) => !isDefined(payRate.ptoBenefitsDiff)) &&
    hasSplitBenefitsDiff;

  useEffect(() => {
    if (requiredHealthBenefits) {
      setValue("useHealthInsuranceBenefit", true);
    } else if (requiredPaidTimeOffBenefits) {
      setValue("usePaidTimeOffBenefit", true);
    } else if (requiredCombinedBenefits && !hasSplitBenefitsDiff) {
      setValue("useCombinedBenefit", true);
    }
  }, [
    requiredCombinedBenefits,
    requiredHealthBenefits,
    requiredPaidTimeOffBenefits,
    setValue,
    hasSplitBenefitsDiff,
  ]);

  const handleCombinedBenefitsChange = (newValue: boolean, otherBenefit?: boolean) => {
    let newCombinedBenefit;
    if (newValue) {
      newCombinedBenefit = true;
      setValue("useCombinedBenefit", newCombinedBenefit);
    } else if (otherBenefit === false) {
      newCombinedBenefit = false;
      setValue("useCombinedBenefit", newCombinedBenefit);
    }

    return newCombinedBenefit;
  };

  const logActionEvent = (params: {
    action: string;
    previousValue?: boolean;
    newValue: boolean;
  }) => {
    const { action, previousValue, newValue } = params;
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAY_AND_SCHEDULE_FORM_ACTIONS, {
      action,
      placementId: placement.id,
      placementCandidateId,
      workerId,
      workplaceId: placement.workplaceId,
      previousValue,
      newValue,
      highestMatchingQualification,
      offeredJobTypes: getOfferedJobTypes({
        placement,
        transparencyPayRates,
      }),
    });
  };

  if (hasSplitBenefitsDiff) {
    return (
      <Stack spacing={6}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={1} sx={{ maxWidth: 210 }}>
            <Text
              variant="body2"
              sx={(theme) => ({
                color: theme.palette.text.primary,
                fontWeight: 500,
              })}
            >
              Include health insurance
            </Text>
            <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
              {requiredHealthBenefits
                ? "This workplace requires employees to opt into health insurance"
                : "Show me rates that include health insurance"}
            </Text>
          </Stack>
          <Controller
            name="useHealthInsuranceBenefit"
            control={control}
            render={({ field }) => (
              <IosSwitch
                aria-label="Enable health benefits package"
                data-testid="include-health-benefits-switch"
                size="large"
                color="primary"
                checked={field.value ?? requiredHealthBenefits}
                disabled={requiredHealthBenefits || isUpdating}
                onChange={(event) => {
                  logActionEvent({
                    action: "include_health_benefits_selected",
                    previousValue: field.value,
                    newValue: event.target.checked,
                  });

                  field.onChange(event.target.checked);
                  const newCombinedBenefit = handleCombinedBenefitsChange(
                    event.target.checked,
                    usePaidTimeOffBenefit
                  );
                  onBenefitsChange({
                    useHealthInsuranceBenefit: event.target.checked,
                    ...(isDefined(newCombinedBenefit) && {
                      useCombinedBenefit: newCombinedBenefit,
                    }),
                  });
                }}
              />
            )}
          />
        </Stack>
        <Divider color="primary" />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={1} sx={{ maxWidth: 210 }}>
            <Text semibold variant="body2">
              Include paid time off
            </Text>
            <Text variant="caption" color={(theme) => theme.palette.text.tertiary}>
              {requiredPaidTimeOffBenefits
                ? "This workplace requires employees to opt into paid time off"
                : "Show me rates that include paid time off"}
            </Text>
          </Stack>
          <Controller
            name="usePaidTimeOffBenefit"
            control={control}
            render={({ field }) => (
              <IosSwitch
                aria-label="Enable PTO benefits package"
                data-testid="include-pto-benefits-switch"
                size="large"
                color="primary"
                checked={field.value ?? requiredPaidTimeOffBenefits}
                disabled={requiredPaidTimeOffBenefits || isUpdating}
                onChange={(event) => {
                  logActionEvent({
                    action: "include_pto_benefits_selected",
                    previousValue: field.value,
                    newValue: event.target.checked,
                  });

                  field.onChange(event.target.checked);
                  const newCombinedBenefit = handleCombinedBenefitsChange(
                    event.target.checked,
                    useHealthInsuranceBenefit
                  );
                  onBenefitsChange({
                    usePaidTimeOffBenefit: event.target.checked,
                    ...(isDefined(newCombinedBenefit) && {
                      useCombinedBenefit: newCombinedBenefit,
                    }),
                  });
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack spacing={1} sx={{ maxWidth: 210 }}>
        <Text semibold variant="body2">
          Include benefits package
        </Text>
        <Text variant="caption" color={(theme) => theme.palette.text.tertiary}>
          {requiredCombinedBenefits
            ? "This workplace requires employees to opt into insurance and paid time off"
            : "View pay rates with insurance and PTO included"}
        </Text>
      </Stack>
      <Controller
        name="useCombinedBenefit"
        control={control}
        render={({ field }) => (
          <IosSwitch
            aria-label="Enable benefits package"
            data-testid="include-benefits-switch"
            size="large"
            color="primary"
            checked={field.value ?? requiredCombinedBenefits}
            disabled={requiredCombinedBenefits || isUpdating}
            onChange={(event) => {
              logActionEvent({
                action: "include_combined_benefits_package_selected",
                previousValue: field.value,
                newValue: event.target.checked,
              });

              field.onChange(event.target.checked);
              setValue("usePaidTimeOffBenefit", event.target.checked);
              setValue("useHealthInsuranceBenefit", event.target.checked);

              onBenefitsChange({
                useCombinedBenefit: event.target.checked,
                useHealthInsuranceBenefit: event.target.checked,
                usePaidTimeOffBenefit: event.target.checked,
              });
            }}
          />
        )}
      />
    </Stack>
  );
}
